/** Pastéis dos labels Ofative — fundo do avatar quando não há foto. */
export const OFATIVE_AVATAR_PASTELS = [
  "#D0E5FF",
  "#F9DCD3",
  "#E6DBF4",
  "#DBECDD",
  "#BFDEDD",
] as const;

export interface ChipPerson {
  id?: string;
  name: string;
  imageUrl: string | null;
  color?: string | null;
}

/** Convidado da Agenda — o jsonb antigo era só e-mail. */
export interface GoogleAttendee {
  email: string;
  displayName: string | null;
}

const MAX_ATTENDEES = 12;

/** Aceita `string[]` legado e `{ email, displayName }[]` da sync nova. */
export function parseGoogleAttendees(raw: unknown): GoogleAttendee[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const people: GoogleAttendee[] = [];
  for (const item of raw) {
    let email = "";
    let displayName: string | null = null;
    if (typeof item === "string") {
      email = item.trim().toLowerCase();
    } else if (item !== null && typeof item === "object" && "email" in item) {
      const record = item as { email?: unknown; displayName?: unknown };
      email = typeof record.email === "string" ? record.email.trim().toLowerCase() : "";
      const name = typeof record.displayName === "string" ? record.displayName.trim() : "";
      displayName = name === "" ? null : name;
    }
    if (!email.includes("@") || seen.has(email)) continue;
    seen.add(email);
    people.push({ email, displayName });
    if (people.length >= MAX_ATTENDEES) break;
  }
  return people;
}

export function avatarFill(person: ChipPerson): string {
  const explicit = person.color?.trim() ?? "";
  if (explicit !== "") return explicit;
  let hash = 0;
  for (const ch of person.name) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  const index = Math.abs(hash) % OFATIVE_AVATAR_PASTELS.length;
  return OFATIVE_AVATAR_PASTELS[index] ?? "#D0E5FF";
}

/** Parte local do e-mail vira um nome curto para o avatar da Agenda. */
export function labelFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const cleaned = local.replace(/[._+-]+/g, " ").trim();
  if (cleaned === "") return email;
  return cleaned
    .split(/\s+/)
    .map((word) => `${word.slice(0, 1).toLocaleUpperCase("pt-BR")}${word.slice(1)}`)
    .join(" ");
}

/** Iniciais de um nome para o avatar empilhado dos chips Ofative. */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return "?";
  const first = parts[0] ?? "";
  if (parts.length === 1) return first.slice(0, 2).toLocaleUpperCase("pt-BR");
  const last = parts[parts.length - 1] ?? "";
  return `${first.slice(0, 1)}${last.slice(0, 1)}`.toLocaleUpperCase("pt-BR");
}

export function displayNameFromParts(
  firstName: string,
  lastName: string,
  fallback: string,
): string {
  const combined = `${firstName.trim()} ${lastName.trim()}`.trim();
  return combined === "" ? fallback : combined;
}
